import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ReplyList, { ReplyItem } from "@/pages/topic/components/ReplyList";
import ReplyForm from "@/pages/topic/components/ReplyForm";
import RelatedTopics from "@/pages/topic/components/RelatedTopics";
import { topics } from "@/mocks/topics";
import { getBoardById, getTopicById } from "@/utils/content";
import { SITE_URL } from "@/utils/site";

export default function TopicDetail() {
  const { id } = useParams();
  const topic = id ? getTopicById(id) : undefined;

  const [liked, setLiked] = useState(false);
  const [favorited, setFavorited] = useState(false);
  const [copied, setCopied] = useState(false);
  const [replies, setReplies] = useState<ReplyItem[]>(
    topic ? topic.replyList.map((reply) => ({ ...reply })) : [],
  );

  const related = useMemo(() => {
    if (!topic) return [];
    const sameBoard = topics.filter((item) => item.board === topic.board && item.id !== topic.id);
    const others = topics.filter((item) => item.board !== topic.board && item.id !== topic.id);
    return [...sameBoard, ...others].slice(0, 4);
  }, [topic]);

  if (!topic) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-[1240px] flex-col items-center justify-center px-4 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
          <i className="ri-file-unknow-line" />
        </span>
        <h1 className="mt-5 text-xl font-semibold text-foreground-950">这个帖子可能已经被删除了</h1>
        <p className="mt-2 text-sm text-foreground-600">
          换个话题看看，讨论版里还有很多正在热闹进行的帖子。
        </p>
        <Link
          to="/topics"
          className="mt-6 flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line" />
          返回讨论版
        </Link>
      </div>
    );
  }

  const board = getBoardById(topic.board);
  const boardName = board?.name ?? topic.boardName;
  const likeCount = topic.likes + (liked ? 1 : 0);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const handleAddReply = (content: string) => {
    setReplies((prev) => [
      ...prev,
      {
        author: "我",
        authorTag: "瓜田新生",
        time: "刚刚",
        content,
        likes: 0,
        isMine: true,
      },
    ]);
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "DiscussionForumPosting",
      headline: topic.title,
      url: `${SITE_URL}/topic/${topic.id}`,
      inLanguage: "zh-CN",
      datePublished: "2026-09-10",
      author: { "@type": "Person", name: topic.author },
      articleBody: topic.content.join(" "),
      interactionStatistic: {
        "@type": "InteractionCounter",
        interactionType: "https://schema.org/CommentAction",
        userInteractionCount: topic.replies,
      },
      about: boardName,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "讨论版", item: `${SITE_URL}/topics` },
        { "@type": "ListItem", position: 3, name: boardName, item: `${SITE_URL}/topics?board=${topic.board}` },
        { "@type": "ListItem", position: 4, name: topic.title, item: `${SITE_URL}/topic/${topic.id}` },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${topic.title} - 瓜田社区${boardName}`}
        description={`${topic.excerpt.slice(0, 90)} 来瓜田社区讨论版参与这个话题，发帖互动、回帖讨论，聊聊你的看法。`}
        keywords={`${topic.tags.join(",")},瓜田社区,瓜田社区回帖讨论`}
        path={`/topic/${topic.id}`}
        ogType="article"
        image={topic.cover}
        jsonLd={jsonLd}
      />

      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1240px] px-4 py-5 md:px-6">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: "讨论版", to: "/topics" },
              { label: boardName, to: `/topics?board=${topic.board}` },
              { label: "帖子正文" },
            ]}
          />
        </div>
      </div>

      <div className="mx-auto max-w-[1240px] px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <article className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className="p-5 md:p-7">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/topics?board=${topic.board}`}
                    className="flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-800 transition-colors hover:bg-primary-200"
                  >
                    <i className="ri-chat-thread-line" />
                    {boardName}
                  </Link>
                  {topic.isHot && (
                    <span className="flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
                      <i className="ri-fire-line" />
                      热帖推荐
                    </span>
                  )}
                </div>

                <h1 className="mt-4 text-xl font-bold leading-snug text-foreground-950 md:text-3xl">
                  {topic.title}
                </h1>

                <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-background-200 pb-5 text-xs text-foreground-500">
                  <span className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-xs font-medium text-background-50">
                      {topic.author.slice(0, 1)}
                    </span>
                    <span className="text-sm font-medium text-foreground-900">{topic.author}</span>
                    <span className="rounded bg-primary-50 px-1.5 py-0.5 text-primary-700">
                      {topic.authorTag}
                    </span>
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line" />
                    发表于 {topic.createdAt}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line" />
                    {topic.views} 浏览
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-smile-2-line" />
                    {replies.length + topic.replies} 回帖
                  </span>
                </div>

                <div className="my-6 overflow-hidden rounded-lg">
                  <img
                    src={topic.cover}
                    alt={`${topic.title} 配图`}
                    title={`${topic.title} - 瓜田社区${boardName}`}
                    className="h-64 w-full object-cover object-top md:h-80"
                  />
                </div>

                <div className="space-y-4 text-[15px] leading-relaxed text-foreground-700">
                  {topic.content.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  {topic.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/topics?q=${encodeURIComponent(tag)}`}
                      className="rounded-md bg-background-100 px-2.5 py-1 text-xs text-foreground-600 transition-colors hover:bg-secondary-100 hover:text-secondary-800"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-background-200 pt-5">
                  <button
                    type="button"
                    onClick={() => setLiked((value) => !value)}
                    className={`flex h-10 items-center gap-2 whitespace-nowrap rounded-md border px-4 text-sm transition-colors ${
                      liked
                        ? "border-accent-300 bg-accent-50 text-accent-700"
                        : "border-background-300 text-foreground-600 hover:border-accent-300 hover:text-accent-600"
                    }`}
                  >
                    <i className={liked ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                    {liked ? "已点赞" : "点赞"} {likeCount}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFavorited((value) => !value)}
                    className={`flex h-10 items-center gap-2 whitespace-nowrap rounded-md border px-4 text-sm transition-colors ${
                      favorited
                        ? "border-secondary-300 bg-secondary-50 text-secondary-800"
                        : "border-background-300 text-foreground-600 hover:border-secondary-300 hover:text-secondary-700"
                    }`}
                  >
                    <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                    {favorited ? "已收藏" : "收藏"}
                  </button>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className={copied ? "ri-check-line" : "ri-link"} />
                    {copied ? "链接已复制" : "分享"}
                  </button>
                  <span className="ml-auto flex items-center gap-1 text-xs text-foreground-400">
                    <i className="ri-shield-check-line" />
                    友好讨论，拒绝人身攻击
                  </span>
                </div>
              </div>

              <div className="border-t border-background-200 bg-background-100/50 p-5 md:p-7">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-semibold text-foreground-950">
                    全部回帖
                    <span className="ml-2 text-sm font-normal text-foreground-500">
                      共 {replies.length + topic.replies} 条
                    </span>
                  </h2>
                  <span className="flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-sort-desc" />
                    按时间排序
                  </span>
                </div>

                <div className="mt-4">
                  <ReplyList replies={replies} />
                </div>

                <ReplyForm onSubmit={handleAddReply} />
              </div>
            </article>
          </div>

          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <RelatedTopics
                items={related.map((item) => ({
                  id: item.id,
                  title: item.title,
                  boardName: item.boardName,
                  replies: item.replies,
                }))}
                boardId={topic.board}
                boardName={boardName}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}